import React, { useState } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Button,
  Menu,
  MenuItem,
  Divider,
  Avatar,
  Chip
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  Build,
  QrCode2,
  Description,
  DirectionsCar,
  People,
  HomeWork,
  ManageAccounts,
  Home,
  Settings,
  Logout,
  Add,
  Person,
  AssignmentTurnedIn,
  History
} from '@mui/icons-material';

const drawerWidth = 240;

function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const menuItems = [
    { text: 'Tableau de bord', icon: <Dashboard />, path: '/dashboard', roles: ['superadmin', 'admin', 'manager', 'tenant', 'contractor'] },
    { text: 'Interventions', icon: <Build />, path: '/interventions', roles: ['superadmin', 'admin', 'manager', 'tenant', 'contractor'] },
    { text: 'QR Codes', icon: <QrCode2 />, path: '/qrcodes', roles: ['superadmin', 'admin', 'manager'] },
    { text: 'Documents', icon: <Description />, path: '/documents', roles: ['superadmin', 'admin', 'manager', 'tenant'] },
    { text: 'Véhicules', icon: <DirectionsCar />, path: '/vehicles', roles: ['superadmin', 'admin', 'manager'] },
    { text: 'Résidences', icon: <HomeWork />, path: '/residences', roles: ['superadmin', 'admin', 'manager'] },
    { text: 'Locataires', icon: <People />, path: '/tenants', roles: ['superadmin', 'admin','manager'] },
    { text: 'Utilisateurs', icon: <ManageAccounts />, path: '/users', roles: ['superadmin', 'admin'] },
    { text: 'Historique accès', icon: <History />, path: '/audit-logs', roles: ['superadmin'] }, 
    { text: 'Paramètres', icon: <Settings />, path: '/settings', roles: ['superadmin', 'admin', 'manager'] },
  ];

  const getRoleColor = (role) => {
    switch(role) {
      case 'superadmin': return 'error';
      case 'admin': return 'error';
      case 'manager': return 'warning';
      case 'contractor': return 'info';
      case 'tenant': return 'success';
      default: return 'default';
    }
  };

  const getRoleLabel = (role) => {
    switch(role) {
      case 'superadmin': return 'Super Admin';
      case 'admin': return 'Administrateur';
      case 'manager': return 'Gestionnaire';
      case 'contractor': return 'Sous-traitant';
      case 'tenant': return 'Locataire';
      case 'technician': return 'Technicien';
      default: return role;
    }
  };

  const drawer = (
    <div>
      <Toolbar>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <img 
            src="/img/squarehabitat.svg" 
	    alt="Square Habitat" 
	    style={{ 
	      height: 36,
	      width: 'auto'
	    }}
	  />
	</Box>
      </Toolbar>
      <Divider />
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Avatar sx={{ bgcolor: 'primary.main' }}>
          {user.firstName?.[0]}{user.lastName?.[0]}
        </Avatar>
        <Box>
          <Typography variant="body2" noWrap>
            {user.firstName} {user.lastName}
          </Typography>
          <Chip
            label={getRoleLabel(user.role)}
            size="small"
            color={getRoleColor(user.role)}
          />
        </Box>
      </Box>
      <Divider />
      <List>
        {menuItems
          .filter(item => item.roles.includes(user.role))
          .map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => navigate(item.path)}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          ))}
      </List>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout}>
            <ListItemIcon><Logout /></ListItemIcon>
            <ListItemText primary="Déconnexion" />
          </ListItemButton>
        </ListItem>
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
	  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
	    <img 
	      src="/img/squarehabitat.svg" 
	      alt="Square Habitat" 
	      style={{ 
	        height: 32,
	        filter: 'brightness(0) invert(1)' // Pour rendre le logo blanc
	      }}
	    />
	    <Typography variant="h6" noWrap component="div">
	      Gestion des Résidences
            </Typography>
	  </Box>
          <Button 
            color="inherit" 
            startIcon={<Add />} 
            sx={{ mr: 2 }}
            onClick={() => navigate('/scan/new')}
          >
            Nouvelle Intervention
          </Button>
          <IconButton
            size="large"
            onClick={handleMenu}
            color="inherit"
          >
            <Person />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            <MenuItem onClick={() => { handleClose(); navigate('/profile'); }}>
              Mon Profil
            </MenuItem>
            <MenuItem onClick={() => { handleClose(); navigate('/settings'); }}>
              Paramètres
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>Déconnexion</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: 8
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}

export default Layout;
